import { defineStore } from "pinia";
import { computed, ref, Ref } from "vue";
import { PersonDTO } from "@/repositories/dto/user/PersonDTO.ts";
import { IPersonState, IUserStep } from "@/repositories/dto/user/IPersonState.ts";
import { IChallengeResponse } from "@/repositories/dto/user/IChallengeResponse.ts";
import { ISolutionResponse } from "@/repositories/dto/user/ISolutionResponse.ts";
import { IRepositoryError } from "@/repositories/dto/errors/IRepositoryError.ts";
import { AuthService, PersonService } from "@/services";
import { IMfaChallengePurpose } from "@/repositories/implementations/UserRepository.ts";
import useClientStore from "@/store/clientStore.ts";

export const usePersonStore = defineStore("person", () => {
  const personService = new PersonService();
  const authService = new AuthService();

  const currentStepKey: Ref<string | null> = ref(null);
  const steps: Ref<IUserStep[]> = ref([]);
  const person: Ref<PersonDTO | null> = ref(null);
  const currentStep: Ref<IUserStep | null> = computed(() => steps.value.find((step) => step.key === currentStepKey.value) ?? null);

  // Check if all mandatory onboarding steps are complete
  const isOnboardingComplete = computed(() => {
    if (!person.value || steps.value.length === 0) {
      return false;
    }
    // All mandatory steps must be valid
    return steps.value
      .filter(step => step.isMandatory)
      .every(step => step.isValid);
  });

  // Check if user is verified (can create clients)
  const isVerified = computed(() => {
    if (!person.value?.profile) {
      return false;
    }
    // User must have VERIFIED status to create clients
    return person.value.profile.status === "VERIFIED";
  });

  const clientStore = useClientStore();

  async function init() {
    await getState();
  }

  function _mapUserState(personState: IPersonState | null): void {
    if (personState !== null) {
      person.value = personState?.person ?? null;
      steps.value = personState?.steps ?? [];
      currentStepKey.value = personState?.currentStep ?? null;
      clientStore.setClients(personState?.clients as any ?? []);
      clientStore.setCurrentClientUuid(personState?.currentClient?.id as any ?? null);
    } else {
      person.value = null;
      steps.value = [];
      clientStore.setClients([]);
      clientStore.setCurrentClientUuid(null);
    }
  }

  async function logout() {
    await authService.logout();
    await this.getState();
  }

  async function getState() {
    try {
      const userState = await personService.getState();
      _mapUserState(userState);
    } catch (e: any) {
      if (e.errorCode === "401") {
        _mapUserState(null);
      } else {
        console.log("e:", e);
      }
    }
  }

  async function getChallenge(purpose: IMfaChallengePurpose, challengeCode: string | null = null): Promise<IChallengeResponse | IRepositoryError> {
    return await personService.getChallenge(purpose, challengeCode);
  }

  async function verifySolution(challengeCode: string, solutionCode: string): Promise<ISolutionResponse | IRepositoryError> {
    const result = await personService.verifySolution(challengeCode, solutionCode);
    await this.getState();
    return result;
  }

  async function addPhone(countryCode: string, phone: string): Promise<IChallengeResponse | IRepositoryError> {
    const result = await personService.addPhone(countryCode, phone);
    await this.getState();
    return result;
  }

  return {
    steps,
    currentStepKey,
    currentStep,
    person,
    isOnboardingComplete,
    isVerified,

    init,
    getState,
    addPhone,
    getChallenge,
    verifySolution,
    logout
  };
});

export default usePersonStore;
